import { useState } from 'react';
import { bulkReportService } from '../../services/bulkReportService';
import type { Twins } from '../../lib/billPipeline';
import type { BvBill } from '../../types/billTypes';

type KeepThisOneProps = { bill: BvBill; twins: Twins; onDone: () => void };

/**
 * On a bill that shares its period with other active bills: keep this one,
 * archive the others and move their emails here (migration 128). Confirmed
 * inline — the artifact viewer and some embeds block window.confirm.
 */
export function KeepThisOne({ bill, twins, onDone }: KeepThisOneProps) {
  const [confirming, setConfirming] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const keep = async () => {
    setWorking(true);
    setError(null);
    const res = await bulkReportService.resolveDuplicate(bill.id);
    setWorking(false);
    if (!res.success) { setError(res.error ?? 'Could not resolve the duplicate'); return; }
    setConfirming(false);
    onDone();
  };

  const others = `${twins.count} other bill${twins.count === 1 ? '' : 's'}`;
  if (!confirming) {
    return (
      <button
        type="button"
        data-testid="bill-runs-keep-this"
        className="mt-1.5 inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-700 hover:border-blue-400 hover:text-blue-700"
        onClick={() => setConfirming(true)}
      >
        Keep this one
      </button>
    );
  }
  return (
    <div data-testid="bill-runs-keep-confirm" className="mt-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
      <p>Archive the {others} for this period and move {twins.count === 1 ? 'its' : 'their'} emails onto this bill?</p>
      {error && <p data-testid="bill-runs-keep-error" className="mt-1 text-red-600">{error}</p>}
      <div className="mt-1 flex gap-1.5">
        <button
          type="button"
          data-testid="bill-runs-keep-confirm-yes"
          disabled={working}
          className="rounded bg-blue-600 px-2 py-0.5 font-medium text-white disabled:opacity-60"
          onClick={() => void keep()}
        >
          {working ? 'Archiving…' : `Keep this, archive ${others}`}
        </button>
        <button type="button" disabled={working} className="rounded px-2 py-0.5 text-gray-600" onClick={() => { setConfirming(false); setError(null); }}>
          Cancel
        </button>
      </div>
    </div>
  );
}
